import { CheckCircleIcon, HeartIcon } from "@phosphor-icons/react";
import { goToUrl } from "apps/visits/core/utils/go-to-url";
import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { Loader } from "apps/visits/packages/ui/src/components";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useEffectOnce } from "react-use";
import { Badge } from "@/core/shared/v2/ui/badge";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import type { ItemListId } from "../../tracking/create-ecommerce-event";
import { Card, GetOrderError, Layout, OrderCart } from "../components";
import { UpsellCard } from "../components/upsell-card";
import { DATE_FORMAT } from "../config/date-time";
import { useOrderData } from "../hooks/use-order-data";
import { useCartStore } from "../store/use-cart-store";
import { useGetConfig } from "../store/use-config";
import { useOrderingStore } from "../store/use-ordering-store";
import { purchaseEvent, safeTrackEvent } from "../tracking/events";
import type { OrderStatus } from "../types/order-status";
import { clearGaUserType, readGaUserType } from "../utils/ga-user-type";
import { clearGaoItemListIds, readGaoItemListIds } from "../utils/gao-item-list-ids";

const ORDER_STATUS_BADGE_VARIANT: Record<OrderStatus, "neutral" | "primary" | "success" | "warning" | "destructive"> = {
  created: "neutral",
  in_realization: "primary",
  completed: "success",
  completed_overdue: "warning",
  cancelled: "destructive",
};

export const ThankYou = () => {
  const { t } = useTranslation();
  const config = useGetConfig();
  const { reset: resetOrderingStore } = useOrderingStore();
  const { reset: resetCart } = useCartStore();
  const friendsAndFamilyConfig = config.vouchersDistributionConfigs.filter(
    (c) => c.configName === "friends_and_family",
  )[0];
  const { uuid } = useParams<{ uuid: string }>();
  const { data, isLoading, error } = useOrderData(uuid);

  useEffectOnce(() => {
    resetOrderingStore();
    resetCart();
  });

  // REV-117: fire purchase exactly once per order uuid; consume ga_user_type so subsequent visits don't reuse it.
  const trackedPurchaseUuidRef = useRef<string>();
  useEffect(() => {
    if (!data?.order) return;
    if (trackedPurchaseUuidRef.current === data.order.uuid) return;
    trackedPurchaseUuidRef.current = data.order.uuid;

    // Cart is reset on TYP mount, so item_list_id source is sessionStorage written during
    // confirm-order onSuccess. Fallback "listing" guarantees every item has an attributable source.
    const itemListIdsByUuid = readGaoItemListIds();
    const items = data.order.items.map((item) => ({
      uuid: item.productUuid,
      name: item.name,
      grossPrice: String(item.grossPrice),
      productType: "blood-test-package" as const,
      quantity: 1,
      item_list_id: itemListIdsByUuid[item.productUuid] ?? ("listing" as ItemListId),
    }));
    const userType = readGaUserType();
    // paymentType is reported only for orders that actually went through a payment provider.
    // Free orders skip PayU entirely; reporting "PayU" for them would skew payment_method splits.
    const totalGrossPrice = Number(data.order.totalGrossPrice);
    const paymentType = totalGrossPrice > 0 ? "PayU" : undefined;

    safeTrackEvent(
      () =>
        purchaseEvent(
          items,
          { value: totalGrossPrice, transactionId: data.order.uuid },
          { paymentType, numberOfProducts: items.length, userType },
        ),
      "purchase",
      "thank-you mount",
    );

    clearGaUserType();
    clearGaoItemListIds();
  }, [data?.order]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-10">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <GetOrderError />;
  }

  const startsAtFormattedDate = dayjs(config.startsAt).format(DATE_FORMAT);
  const endsAtFormattedDate = dayjs(config.endsAt).format(DATE_FORMAT);
  const companySignUpListText = t("cmsTranslations.thank_you_page.other.company_sign_up_list");

  return (
    <IntersectionFadeIn>
      <Layout className="lg:max-w-5xl">
        <Card className="mt-4 md:mt-12">
          <div className="mb-6 flex flex-col items-center justify-center gap-x-4 gap-y-1 md:flex-row">
            <CheckCircleIcon weight="fill" className="size-12 text-green-600" />
            <div>
              <h1 className="text-center font-semibold text-xl lg:text-2xl">
                {t("GROUP_ACTIONS_ORDERING.THANK_YOU.ORDER_SUCCESS")}
              </h1>
              <span className="block text-center text-sm text-zinc-500 lg:text-left">
                {t("GROUP_ACTIONS_ORDERING.THANK_YOU.ORDER_SUCCESS_DESCRIPTION")}
              </span>
            </div>
          </div>

          <div className="rounded-lg px-4 pt-4 pb-0 md:px-8 md:py-8">
            <h2 className="mb-4 font-semibold text-lg">{t("GROUP_ACTIONS_ORDERING.THANK_YOU.ORDER_SUMMARY")}</h2>
            <OrderCart items={data?.order.items} totalPrice={data?.order.totalGrossPrice} />
          </div>
          <div className="mt-6 flex flex-col gap-2">
            <p className="flex items-center gap-3 text-sm text-zinc-500">
              {t("GROUP_ACTIONS_ORDERING.THANK_YOU.ORDER_STATUS")}:
              {data?.order.status && (
                <Badge variant={ORDER_STATUS_BADGE_VARIANT[data.order.status]} size="sm">
                  {t(`GROUP_ACTIONS_ORDERING.THANK_YOU.ORDER_STATUSES.${data.order.status.toUpperCase()}`)}
                </Badge>
              )}
            </p>
            <p className="flex flex-col gap-x-3 font-medium text-sm text-zinc-500 md:flex-row md:items-center">
              {t("GROUP_ACTIONS_ORDERING.THANK_YOU.ACTION_DURATION")}:{" "}
              <span className="font-medium text-base text-zinc-900">
                {startsAtFormattedDate} – {endsAtFormattedDate}
              </span>
            </p>
            {companySignUpListText && <p className="font-medium text-sm text-zinc-500">{companySignUpListText}</p>}
          </div>
        </Card>
        <div className="mt-6 flex flex-col gap-4 md:flex-row">
          {friendsAndFamilyConfig && (
            <UpsellCard
              header={t("cmsTranslations.thank_you_page.vouchers_offer.title")}
              description={t("cmsTranslations.thank_you_page.vouchers_offer.description")}
              buttonText={t("cmsTranslations.thank_you_page.vouchers_offer.button")}
              icon={<HeartIcon weight="fill" />}
              onClick={() => goToUrl(friendsAndFamilyConfig.path)}
            />
          )}
          {/* TODO: uncomment when we have a correct link */}
          {/* <UpsellCard
					header={t("cmsTranslations.thank_you_page.prepare_for_service.title")}
					description={t("cmsTranslations.thank_you_page.prepare_for_service.description")}
					buttonText={t("cmsTranslations.thank_you_page.prepare_for_service.button")}
					icon={<FirstAidKitIcon />}
					onClick={() => goToUrl(prepareForGroupActionLink)}
				/> */}
        </div>
      </Layout>
    </IntersectionFadeIn>
  );
};
